import { useMemo, useState } from 'react';
import type { JsonObject } from '@inf-monkeys-tech/monkeys';
import { BaseButton, BaseDialog } from '../base';
import { CapabilityStatus } from './CapabilityStatus';
import type { DesignCapabilityRendererContext } from './contracts';
import { DESIGN_CAPABILITY_I18N_KEYS } from './i18n';

export interface NumericSeriesPoint { id: string; x: number; y: number }
export interface NumericSeriesModel {
  seriesId: string;
  labelKey: string;
  xLabelKey: string;
  yLabelKey: string;
  points: NumericSeriesPoint[];
}
const text = { type: 'string', minLength: 1, maxLength: 256 };
export const NUMERIC_SERIES_SCHEMA = 'monkeys-design://render-model/numeric-series/v1';
export const NUMERIC_SERIES_RENDER_MODEL_SCHEMA: JsonObject = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  $id: NUMERIC_SERIES_SCHEMA,
  type: 'object', additionalProperties: false,
  required: ['seriesId', 'labelKey', 'xLabelKey', 'yLabelKey', 'points'],
  properties: {
    seriesId: text, labelKey: text, xLabelKey: text, yLabelKey: text,
    points: { type: 'array', maxItems: 10000, items: {
      type: 'object', additionalProperties: false, required: ['id', 'x', 'y'],
      properties: { id: text, x: { type: 'number' }, y: { type: 'number' } },
    } },
  },
};
export const NUMERIC_SERIES_RANGE_INTENT_SCHEMA: JsonObject = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  $id: 'monkeys-design://intent/numeric-series-range/v1',
  type: 'object', additionalProperties: false, required: ['seriesId', 'start', 'end'],
  properties: { seriesId: text, start: { type: 'number', minimum: 0, maximum: 100 }, end: { type: 'number', minimum: 0, maximum: 100 } },
};
export const NUMERIC_SERIES_EXPAND_INTENT_SCHEMA: JsonObject = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  $id: 'monkeys-design://intent/numeric-series-expand/v1',
  type: 'object', additionalProperties: false, required: ['seriesId', 'expanded'],
  properties: { seriesId: text, expanded: { type: 'boolean' } },
};
const object = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object' && !Array.isArray(value);
export function isNumericSeriesModel(value: unknown): value is NumericSeriesModel {
  if (!object(value) || !['seriesId', 'labelKey', 'xLabelKey', 'yLabelKey'].every(key => typeof value[key] === 'string' && value[key].length > 0 && value[key].length <= 256) || !Array.isArray(value.points) || value.points.length > 10000) return false;
  const ids = new Set<string>();
  return value.points.every(point => {
    if (!object(point) || typeof point.id !== 'string' || !point.id || point.id.length > 256 || ids.has(point.id) || typeof point.x !== 'number' || typeof point.y !== 'number' || !Number.isFinite(point.x) || !Number.isFinite(point.y)) return false;
    ids.add(point.id); return true;
  });
}
function NumericSeriesView({ model, translate, emitIntent }: Pick<DesignCapabilityRendererContext, 'translate' | 'emitIntent'> & { model: NumericSeriesModel }) {
  const [range, setRange] = useState({ start: 0, end: 100 });
  const [expanded, setExpanded] = useState(false);
  const [pointIndex, setPointIndex] = useState(0);
  const labels = DESIGN_CAPABILITY_I18N_KEYS.numericSeries;
  const updateRange = (start: number, end: number) => {
    if (!Number.isFinite(start) || !Number.isFinite(end) || start < 0 || end > 100 || start >= end) return;
    setRange({ start, end });
    setPointIndex(0);
    emitIntent('capability.numeric-series.range-change', { seriesId: model.seriesId, start, end });
  };
  const updateExpanded = (next: boolean) => {
    setExpanded(next);
    emitIntent('capability.numeric-series.expand', { seriesId: model.seriesId, expanded: next });
  };
  const points = useMemo(() => [...model.points].sort((a, b) => a.x - b.x), [model.points]);
  const minX = points[0].x;
  const lastX = points[points.length - 1].x;
  const maxX = lastX === minX ? minX + 1 : lastX;
  const lower = minX + (maxX - minX) * range.start / 100;
  const upper = minX + (maxX - minX) * range.end / 100;
  const visible = points.filter(point => point.x >= lower && point.x <= upper);
  const selectedIndex = Math.min(pointIndex, Math.max(0, visible.length - 1));
  const selected = visible[selectedIndex];
  const pointLabel = selected ? translate(labels.point, { xLabel: translate(model.xLabelKey), x: selected.x, yLabel: translate(model.yLabelKey), y: selected.y }) : translate(labels.noPoints);
  const values = visible.length ? visible : points;
  let minY = Infinity, maxY = -Infinity;
  for (const point of values) { minY = Math.min(minY, point.y); maxY = Math.max(maxY, point.y); }
  const padding = minY === maxY ? Math.max(Math.abs(minY) * 0.05, 1e-12) : (maxY - minY) * 0.05;
  minY -= padding; maxY += padding;
  const x = (value: number) => 70 + (value - lower) / (upper - lower) * 680;
  const y = (value: number) => 330 - (value - minY) / (maxY - minY) * 280;
  const plot = (large: boolean) => <div className="grid gap-3">
    <svg viewBox="0 0 820 390" role="img" aria-label={translate(model.labelKey)} className={large ? 'w-full min-h-96' : 'w-full'}
      onWheel={event => {
        if (!event.ctrlKey) return;
        event.preventDefault();
        const midpoint = (range.start + range.end) / 2;
        const span = Math.min(100, Math.max(1, (range.end - range.start) * (event.deltaY > 0 ? 1.2 : 0.8)));
        const start = Math.max(0, Math.min(100 - span, midpoint - span / 2));
        updateRange(start, start + span);
      }}>
      <line x1="70" y1="330" x2="750" y2="330" className="stroke-border" />
      <line x1="70" y1="50" x2="70" y2="330" className="stroke-border" />
      {[0, 0.25, 0.5, 0.75, 1].map(tick => <g key={tick} className="fill-muted-foreground text-xs">
        <text x={70 + tick * 680} y="352" textAnchor="middle">{Number((lower + (upper - lower) * tick).toPrecision(5))}</text>
        <text x="62" y={334 - tick * 280} textAnchor="end">{Number((minY + (maxY - minY) * tick).toPrecision(5))}</text>
      </g>)}
      <text x="410" y="380" textAnchor="middle" className="fill-muted-foreground text-xs">{translate(model.xLabelKey)}</text>
      <text x="16" y="190" transform="rotate(-90 16 190)" textAnchor="middle" className="fill-muted-foreground text-xs">{translate(model.yLabelKey)}</text>
      <polyline points={visible.map(point => `${x(point.x)},${y(point.y)}`).join(' ')} fill="none" strokeWidth="2" className="stroke-primary" />
      {selected ? <circle cx={x(selected.x)} cy={y(selected.y)} r="4" className="fill-primary stroke-foreground" aria-label={pointLabel}><title>{pointLabel}</title></circle> : null}
    </svg>
    <div className="flex flex-wrap items-center gap-3">
      <label className="flex items-center gap-2">{translate(labels.inspect)}<input type="range" min="0" max={Math.max(0, visible.length - 1)} step="1" value={selectedIndex} disabled={!visible.length} aria-valuetext={pointLabel} onChange={event => setPointIndex(Number(event.target.value))} /></label>
      <output aria-live="polite">{pointLabel}</output>
    </div>
    <div className="flex flex-wrap items-center gap-3">
      <label className="flex items-center gap-2">{translate(labels.start)}<input type="range" min="0" max={range.end - 0.1} step="0.1" value={range.start} onChange={event => updateRange(Number(event.target.value), range.end)} /></label>
      <label className="flex items-center gap-2">{translate(labels.end)}<input type="range" min={range.start + 0.1} max="100" step="0.1" value={range.end} onChange={event => updateRange(range.start, Number(event.target.value))} /></label>
      <BaseButton label={translate(labels.reset)} onClick={() => updateRange(0, 100)} />
    </div>
  </div>;
  return <section data-numeric-series={model.seriesId} className="rounded-lg border border-border bg-card p-4">
    <div className="flex items-center justify-between gap-3">
      <h3>{translate(model.labelKey)}</h3>
      <BaseDialog open={expanded} onOpenChange={updateExpanded} triggerLabel={translate(labels.expand)} title={translate(model.labelKey)} closeLabel={translate(labels.close)} portal classNames={{ panel: 'sm:max-w-6xl' }}>{expanded ? plot(true) : null}</BaseDialog>
    </div>
    {plot(false)}
  </section>;
}
export function NumericSeriesCapabilityRenderer(context: DesignCapabilityRendererContext) {
  const { access, loading, errorCode, model, empty, translate } = context;
  if (!access.allowed) return <CapabilityStatus kind="forbidden" translate={translate} code={access.reasonCode} />;
  if (loading) return <CapabilityStatus kind="loading" translate={translate} />;
  if (errorCode) return <CapabilityStatus kind="error" translate={translate} code={errorCode} />;
  if (!isNumericSeriesModel(model)) return <CapabilityStatus kind="error" translate={translate} code="capability.numeric-series.invalid-model" />;
  if (empty || !model.points.length) return <CapabilityStatus kind="empty" translate={translate} />;
  return <NumericSeriesView key={model.seriesId} model={model} translate={translate} emitIntent={context.emitIntent} />;
}
